import { useState } from 'react';
import { Tabs, Table, Button, Dropdown, Checkbox, Icon } from 'ui';
import { MenuProps } from 'antd';
import dayjs from 'dayjs';
import { Coupon } from '../../../interfaces/Coupon';
import DiscountType from '../../../enums/discount-type';
import useQueryApiClient from '../../../utils/useQueryApiClient';
import { useTranslations } from './hooks/use-translations';
import { useAuth } from 'hooks/useAuth';
import DefaultLayout from 'admin/components/DefaultLayout';
import CouponCreateUpdateDrawer from './CouponCreateUpdateDrawer';
import { DownOutlined } from '@ant-design/icons';
import { ResponsiveWrapper } from 'admin/components/ResponsiveWrapper';
import MobileList from 'admin/components/MobileLayout/MobileList';
import {
  StyledMobileHeaderWrapper,
  StyledMobilePageTitle,
} from 'admin/components/MobileLayout/style';
import type { MenuInfo } from 'rc-menu/lib/interface';
import { hasAbility } from 'utils/userHelper';
import { StyledCouponsPage } from './style';

const CouponsPage = () => {
  const [refreshKey, setRefreshKey] = useState(0);
  const [isDrawerVisible, setIsDrawerVisible] = useState<boolean>(false);
  const [selectedEntry, setSelectedEntry] = useState<number>(0);
  const t = useTranslations();
  const { user } = useAuth();
  const farmId = user?.farmId;

  const { appendData: deleteCoupon } = useQueryApiClient({
    request: {
      url: 'api/v2/farms/' + farmId + '/coupons/:id',
      method: 'DELETE',
    },
    onSuccess: () => setRefreshKey((k) => k + 1),
  });

  const onClose = () => {
    setRefreshKey((k) => k + 1);
    setIsDrawerVisible(false);
    setSelectedEntry(0);
  };

  const handleActionMenuClick = (menuInfo: MenuInfo, coupon: Coupon) => {
    switch (menuInfo.key) {
      case 'edit':
        setSelectedEntry(coupon.id);
        setIsDrawerVisible(true);
        break;
      case 'delete':
        deleteCoupon([], { id: coupon.id });
        break;
      default:
        break;
    }
  };

  const columns = [
    { title: t('coupons.table.name'), dataIndex: 'name' },
    { title: t('coupons.table.coupon'), dataIndex: 'code' },
    {
      title: t('coupons.table.discount'),
      dataIndex: 'discount_value',
      render: (value: number, coupon: Coupon) => {
        return coupon.discount_type === DiscountType.PERCENT
          ? `– ${Math.trunc(value)}%`
          : `– ${value}€`;
      },
    },
    {
      title: t('coupons.table.apply_to_whole_cart'),
      dataIndex: 'apply_to_whole_cart',
      render: (value: boolean) => <Checkbox checked={value} disabled />,
    },
    {
      title: t('coupons.table.start_date'),
      dataIndex: 'start_date',
      render: (_: string, coupon: Coupon) => {
        return coupon.start_date
          ? dayjs(coupon.start_date).format('DD.MM.YYYY')
          : '-';
      },
    },
    {
      title: t('coupons.table.end_date'),
      dataIndex: 'end_date',
      render: (_: string, coupon: Coupon) => {
        return coupon.end_date
          ? dayjs(coupon.end_date).format('DD.MM.YYYY')
          : '-';
      },
    },
    {
      title: t('coupons.table.status'),
      dataIndex: 'active',
      render: (value: boolean) => <Checkbox checked={value} disabled />,
    },
    {
      title: t('coupons.table.used_count'),
      dataIndex: 'usage',
      render: (count: number) => `x ${count || 0}`,
    },
    {
      title: t('coupons.table.actions'),
      dataIndex: 'actions',
      render: (_: string, coupon: Coupon) => {
        const items: MenuProps['items'] = [
          {
            key: 'edit',
            label: t('general.edit'),
            onClick: () => {
              setSelectedEntry(coupon.id);
              setIsDrawerVisible(true);
            },
          },
          {
            key: 'delete',
            label: t('general.delete'),
            onClick: () => deleteCoupon([], { id: coupon.id }),
          },
        ];

        return (
          <Dropdown
            className="actions"
            menu={{ items }}
            trigger={['click']}
            key={coupon.id}
          >
            <Button
              className="action"
              label={t('general.actions')}
              icon={<DownOutlined />}
              iconPosition="end"
              size="middle"
              ability={'edit_coupon'}
            />
          </Dropdown>
        );
      },
    },
  ];

  const mobileItems = {
    coverFields: [
      {
        name: 'name',
        render: (value: string) => value,
      },
      {
        name: 'code',
        render: (value: string) => value,
      },
    ],
    bodyFields: [
      {
        name: 'discount',
        label: t('coupons.table.discount'),
        render: (value: number, record: Coupon) => {
          return record.discount_type === DiscountType.PERCENT
            ? `– ${Math.trunc(record.discount_value)}%`
            : `– ${record.discount_value}€`;
        },
      },
      {
        name: 'apply_to_whole_cart',
        label: t('coupons.table.apply_to_whole_cart'),
        render: (value: boolean) => <Checkbox checked={value} disabled />,
      },
      {
        name: 'start_date',
        label: t('coupons.table.start_date'),
        render: (_: string, record: Coupon) => {
          return record.start_date
            ? dayjs(record.start_date).format('DD.MM.YYYY')
            : '-';
        },
      },
      {
        name: 'end_date',
        label: t('coupons.table.end_date'),
        render: (_: string, record: Coupon) => {
          return record.end_date
            ? dayjs(record.end_date).format('DD.MM.YYYY')
            : '-';
        },
      },
      {
        name: 'active',
        label: t('coupons.table.status'),
        render: (value: boolean) => <Checkbox checked={value} disabled />,
      },
      {
        name: 'usage',
        label: t('coupons.table.used_count'),
        render: (count: number) => `x ${count || 0}`,
      },
    ],
    footerFields: [
      {
        name: 'actions',
        render: (value: undefined, record: Coupon) => {
          const dropdownTableItems: MenuProps['items'] = [];

          if (hasAbility(user, 'edit_coupon')) {
            dropdownTableItems.push({
              label: t('general.edit'),
              key: 'edit',
            });
          }

          if (hasAbility(user, 'edit_coupon')) {
            dropdownTableItems.push({
              label: t('general.delete'),
              key: 'delete',
              danger: true,
            });
          }

          if (dropdownTableItems.length === 0) {
            return null;
          }

          const menuPropsTable: MenuProps = {
            items: dropdownTableItems,
            onClick: (info) => handleActionMenuClick(info, record),
          };

          return (
            <Dropdown menu={menuPropsTable}>
              <Button
                label={t('general.actions')}
                icon={<DownOutlined />}
                iconPosition={'end'}
                size={'small'}
                className={'actions'}
              />
            </Dropdown>
          );
        },
      },
    ],
  };

  const items = [
    {
      key: 'coupons',
      label: t('navigation.coupons'),
      children: (
        <ResponsiveWrapper
          desktopComponent={
            <Table
              reload={refreshKey}
              url={`/api/v2/farms/${farmId}/coupons`}
              columns={columns}
              page_size={10}
            />
          }
          mobileComponent={
            <MobileList
              url={`/api/v2/farms/${farmId}/coupons`}
              coverFields={mobileItems.coverFields}
              bodyFields={mobileItems.bodyFields}
              footerFields={mobileItems.footerFields}
              reload={refreshKey}
              page_size={10}
            />
          }
        />
      ),
    },
  ];

  return (
    <StyledCouponsPage>
      <StyledMobileHeaderWrapper>
        <Button
          type={'text'}
          label={`+ ${t('coupons.button.add')}`}
          onClick={() => setIsDrawerVisible(true)}
          ability={'edit_coupon'}
          icon={
            <Icon
              name={'admin/coupons-white-no-opacity'}
              className="admin-header-item-icon"
            />
          }
        />
        <div className="mobile-header-item-wrapper" />
      </StyledMobileHeaderWrapper>
      <DefaultLayout.PageLayout
        breadcrumbs={[
          { title: t('navigation.beginning') },
          { title: t('navigation.coupons') },
        ]}
      >
        <DefaultLayout.PageContent>
          <StyledMobilePageTitle>
            <span>{t('navigation.coupons')}</span>
          </StyledMobilePageTitle>
          <Tabs
            tabBarExtraContent={
              <div className={'hide-mobile'}>
                <Button
                  type="primary"
                  label={t('general.add')}
                  onClick={() => setIsDrawerVisible(true)}
                  ability={'edit_coupon'}
                />
              </div>
            }
            items={items}
          />
          <CouponCreateUpdateDrawer
            key={'coupon-drawer'}
            isVisible={isDrawerVisible}
            id={selectedEntry}
            onClose={onClose}
          />
        </DefaultLayout.PageContent>
      </DefaultLayout.PageLayout>
    </StyledCouponsPage>
  );
};

export default CouponsPage;
